/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import React, { useState, useEffect, useCallback } from "react"
import { observer } from "mobx-react"
import {
    Box,
    Card,
    CardContent,
    Typography,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Button,
    Alert,
    Chip,
} from "@mui/material"
import { Delete as DeleteIcon, Add as AddIcon } from "@mui/icons-material"

import { ChatbotTestConfigurationService } from "src/api/services/ChatbotTestConfigurationService"
import type { adminv3_ChatbotTestConfigurationResponse } from "src/api/models/adminv3_ChatbotTestConfigurationResponse"

// import { useStore } from "src/store/lib/useStore"
// import { GlobalStore } from "src/store"

interface ITestConfigurationListProps {
    selectedConfiguration: adminv3_ChatbotTestConfigurationResponse | null
    onConfigurationSelect: (
        config: adminv3_ChatbotTestConfigurationResponse | null,
    ) => void
    refreshTrigger: number
    onRefreshTriggerChange: () => void
}

export const TestConfigurationList: React.FC<ITestConfigurationListProps> =
    observer(
        ({
            selectedConfiguration,
            onConfigurationSelect,
            refreshTrigger,
            onRefreshTriggerChange,
        }) => {
            const [configurations, setConfigurations] = useState<
                adminv3_ChatbotTestConfigurationResponse[]
            >([])
            const [isLoading, setIsLoading] = useState(false)
            const [error, setError] = useState<string | null>(null)

            const loadConfigurations = useCallback(async () => {
                setIsLoading(true)
                setError(null)

                try {
                    // Note: Access group filtering may need to be handled differently
                    // in the new API structure. For now, fetching all configurations.
                    const response =
                        await ChatbotTestConfigurationService.postV3AdminChatbotTestConfigurationList(
                            {
                                request: {
                                    page_number: 1, // API uses 1-based page numbers
                                    page_size: 100,
                                },
                            },
                        )
                    const items = Array.isArray(response.items)
                        ? response.items
                        : []
                    setConfigurations(items)
                } catch (err) {
                    setError(
                        err instanceof Error
                            ? err.message
                            : "Failed to load configurations",
                    )
                } finally {
                    setIsLoading(false)
                }
            }, [])

            useEffect(() => {
                void loadConfigurations()
            }, [loadConfigurations, refreshTrigger])

            const handleDelete = useCallback(
                async (id: number) => {
                    if (
                        !window.confirm(
                            "Are you sure you want to delete this configuration?",
                        )
                    ) {
                        return
                    }

                    try {
                        await ChatbotTestConfigurationService.deleteV3AdminChatbotTestConfiguration(
                            {
                                id: id,
                            },
                        )

                        // Remove from local state
                        setConfigurations((prev) =>
                            prev.filter((config) => config.id !== id),
                        )

                        // If this was the selected configuration, deselect it
                        if ((selectedConfiguration?.id ?? -1) === id) {
                            onConfigurationSelect(null)
                        }

                        onRefreshTriggerChange()
                    } catch (err) {
                        setError(
                            err instanceof Error
                                ? err.message
                                : "Failed to delete configuration",
                        )
                    }
                },
                [
                    selectedConfiguration,
                    onConfigurationSelect,
                    onRefreshTriggerChange,
                ],
            )

            const handleCreateNew = useCallback(() => {
                onConfigurationSelect(null)
            }, [onConfigurationSelect])

            return (
                <Card>
                    <CardContent>
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                mb: 2,
                            }}
                        >
                            <Typography variant="h6">
                                Test Configurations
                            </Typography>
                            <Button
                                startIcon={<AddIcon />}
                                onClick={handleCreateNew}
                                variant="outlined"
                                size="small"
                            >
                                New
                            </Button>
                        </Box>

                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                        )}

                        {isLoading ? (
                            <Typography>Loading configurations...</Typography>
                        ) : configurations.length === 0 ? (
                            <Typography color="text.secondary">
                                No configurations found. Create your first test
                                configuration to get started.
                            </Typography>
                        ) : (
                            <List>
                                {configurations.map((config) => (
                                    <ListItem
                                        key={config.id}
                                        button
                                        selected={
                                            selectedConfiguration?.id ===
                                            config.id
                                        }
                                        onClick={() =>
                                            onConfigurationSelect(config)
                                        }
                                        sx={{
                                            border: 1,
                                            borderColor: "divider",
                                            borderRadius: 1,
                                            mb: 1,
                                            "&.Mui-selected": {
                                                backgroundColor:
                                                    "primary.light",
                                                "&:hover": {
                                                    backgroundColor:
                                                        "primary.light",
                                                },
                                            },
                                        }}
                                    >
                                        <ListItemText
                                            primary={
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 1,
                                                    }}
                                                >
                                                    <Typography variant="subtitle2">
                                                        {config.name}
                                                    </Typography>
                                                    {config.access_group_id && (
                                                        <Chip
                                                            label={`Group ${config.access_group_id}`}
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                    )}
                                                </Box>
                                            }
                                            secondary={
                                                <Box>
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                    >
                                                        {config.description ||
                                                            "No description"}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                    >
                                                        Created:{" "}
                                                        {config.created_at !=
                                                        null
                                                            ? new Date(
                                                                  config.created_at,
                                                              ).toLocaleDateString()
                                                            : "Unknown"}
                                                    </Typography>
                                                </Box>
                                            }
                                        />
                                        <ListItemSecondaryAction>
                                            <IconButton
                                                edge="end"
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    if (config.id != null) {
                                                        // prettier-ignore
                                                        void handleDelete(
                                                            config.id,
                                                        )
                                                    }
                                                }}
                                                color="error"
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </ListItemSecondaryAction>
                                    </ListItem>
                                ))}
                            </List>
                        )}
                    </CardContent>
                </Card>
            )
        },
    )

export default TestConfigurationList
